import { useEffect, useState } from "react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ShieldCheck, ExternalLink, MapPin, AlertTriangle, BookOpen as BookIcon, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Public Skiltforskriften gallery — every official Norwegian sign we
 * carry, grouped by family. Source attribution shown per sign.
 */
export default function Signs() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [activeFam, setActiveFam] = useState("all");
  const [curated, setCurated] = useState({});  // sign_id -> {name, description}
  const [openCode, setOpenCode] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get("/signs/gallery").then(r => setGallery(r.data.families));
    api.get("/signs").then(r => {
      const map = {};
      r.data.forEach(s => { map[s.sign_id] = s; });
      setCurated(map);
    });
  }, []);

  const openSign = (code, image) => {
    setOpenCode(code);
    setOpenImage(image);
    setDetail(null);
    setDetailLoading(true);
    api.get(`/signs/detail/${encodeURIComponent(code)}?lang=${lang}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const totalCount = gallery.reduce((s, f) => s + f.count, 0);
  const familiesToShow = activeFam === "all" ? gallery : gallery.filter(f => f.family_id === activeFam);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
          <h1 data-testid="signs-title" className="text-3xl sm:text-4xl font-bold tracking-tight">
            {t("signsTitle")}
          </h1>
          <a
            href="/SIGNS_AUTHENTICITY.md"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition-colors"
            data-testid="signs-authenticity-badge"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {totalCount} verified official signs
          </a>
        </div>
        <p className="text-gray-600 mb-6 max-w-3xl">{t("signsSubtitle")}</p>

        <Tabs value={activeFam} onValueChange={setActiveFam} className="mb-8">
          <TabsList className="flex flex-wrap h-auto bg-gray-100 p-1">
            <TabsTrigger
              value="all"
              data-testid="signs-fam-all"
              className="data-[state=active]:bg-white data-[state=active]:text-fjord"
            >
              All ({totalCount})
            </TabsTrigger>
            {gallery.map(f => (
              <TabsTrigger
                key={f.family_id}
                value={f.family_id}
                data-testid={`signs-fam-${f.family_id}`}
                disabled={f.count === 0}
                className="data-[state=active]:bg-white data-[state=active]:text-fjord"
              >
                {f.family_label.split(" (")[0]} ({f.count})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {familiesToShow.map(fam => (
          <section
            key={fam.family_id}
            data-testid={`signs-family-${fam.family_id}`}
            className="mb-10"
          >
            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-heading text-lg font-semibold text-gray-900">
                {fam.family_label}
              </h2>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                {fam.rule_reference}
              </Badge>
            </div>

            {fam.count === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No assets in this family yet — see SIGNS_AUTHENTICITY.md for the planned sources.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {fam.signs.map(s => {
                  const c = curated[s.code];
                  return (
                    <Card
                      key={s.code}
                      data-testid={`sign-${s.code}`}
                      onClick={() => openSign(s.code, s.image)}
                      className="p-4 bg-white border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="aspect-square bg-gray-50 rounded-md grid place-items-center mb-3 p-3">
                        <img
                          src={s.image}
                          alt={c?.name || s.label}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-fjord font-semibold mb-1">
                        {s.code}
                      </div>
                      <h3 className="font-heading text-sm font-semibold mb-1 leading-snug">
                        {c?.name || s.label}
                      </h3>
                      {c?.description ? (
                        <p className="text-xs text-gray-600 leading-snug">{c.description}</p>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                          {s.source === "wikimedia_commons" ? (
                            <a
                              href={s.source_url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1 hover:text-fjord"
                              data-testid={`sign-source-${s.code}`}
                            >
                              Wikimedia source
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span>Statens vegvesen / Lovdata</span>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </main>

      {/* Sign detail modal */}
      <Dialog open={!!openCode} onOpenChange={(o) => !o && setOpenCode(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="sign-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-fjord text-xl">{openCode}</span>
              {detail?.reference && (
                <Badge variant="outline" className="text-[10px]">📖 {detail.reference}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-5 items-start mt-2">
            {openImage && (
              <div className="w-32 h-32 bg-gray-50 rounded-md grid place-items-center p-3 border border-gray-200 shrink-0">
                <img src={openImage} alt={openCode} className="max-w-full max-h-full object-contain"/>
              </div>
            )}
            <div className="flex-1 min-w-0">
              {detailLoading && (
                <p className="text-sm text-gray-400" data-testid="sign-detail-loading">Loading…</p>
              )}
              {!detailLoading && !detail && (
                <p className="text-sm text-gray-500" data-testid="sign-detail-pending">
                  Detailed explanation is coming soon. The sign image and code are authoritative per Skiltforskriften.
                </p>
              )}
              {detail?.definition && (
                <p className="text-sm text-gray-900 leading-relaxed">{detail.definition}</p>
              )}
            </div>
          </div>
          {detail && (
            <div className="space-y-3 mt-4">
              {detail.when_you_see_it && (
                <div className="bg-fjord/5 border border-fjord/20 rounded-md p-3" data-testid="sign-when-you-see-it">
                  <div className="text-[10px] uppercase tracking-wide text-fjord mb-1 font-semibold">When you see it</div>
                  <p className="text-sm text-gray-800">{detail.when_you_see_it}</p>
                </div>
              )}
              {detail.what_to_do && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="sign-what-to-do">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1 font-semibold">What to do</div>
                  <p className="text-sm text-emerald-900">{detail.what_to_do}</p>
                </div>
              )}
              {detail.real_situations?.length > 0 && (
                <div data-testid="sign-real-situations">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-2 font-semibold">
                    <MapPin className="w-3 h-3"/> Real situations
                  </div>
                  <div className="space-y-2">
                    {detail.real_situations.map((s, i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded-md p-3">
                        <div className="font-semibold text-sm text-gray-900 mb-0.5">{s.title}</div>
                        <p className="text-xs text-gray-700">{s.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.common_confusions?.length > 0 && (
                <div data-testid="sign-confusions">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-700 mb-2 font-semibold">
                    <AlertTriangle className="w-3 h-3"/> Often confused with
                  </div>
                  <div className="space-y-2">
                    {detail.common_confusions.map((c, i) => (
                      <div key={i} className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                        <div className="text-amber-900"><span className="font-semibold">vs.</span> {c.confused_with}</div>
                        <p className="text-xs text-amber-800 mt-0.5">{c.difference}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.related_signs?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <BookIcon className="w-3 h-3 text-gray-400"/>
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">Related:</span>
                  {detail.related_signs.map(c => (
                    <button
                      key={c}
                      onClick={() => openSign(c, null)}
                      className="text-[10px] font-mono text-fjord bg-fjord/5 border border-fjord/20 rounded px-2 py-0.5 hover:bg-fjord/10"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button
                  data-testid="ask-tutor-about-sign"
                  onClick={() => {
                    const refTxt = detail.reference || `Skiltforskriften — skilt ${openCode}`;
                    const q = lang === "ar"
                      ? `أخبرني عن ${refTxt} (الإشارة ${openCode}) مع مثال واقعي قد أراه في أوسلو.`
                      : lang === "so"
                      ? `Ii sheeg ${refTxt} (calaamada ${openCode}) oo ku tus tusaale dhab ah oo aan ku arki karo Oslo.`
                      : `Tell me about ${refTxt} (sign ${openCode}) with a real example I might see in Oslo.`;
                    navigate(`/tutor?prefill=${encodeURIComponent(q)}`);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-fjord hover:text-fjord-700 hover:underline font-medium"
                >
                  <Sparkles className="w-3.5 h-3.5"/> Ask the AI tutor about this sign
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
